import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDate, type Langue } from '@artosera/shared';
import { Button } from '@artosera/ui';
import { cleMetier } from '../../../api/cles.js';
import { CLE_SESSION } from '../../../auth/queryClient.js';
import { galerieALaction, galerieAModule } from '../../../auth/sujet.js';
import type { SessionUtilisateur } from '../../../api/types.js';
import { useShellContext } from '../../shell/context.js';
import { ErreurPresentation } from '../ErreurPresentation.js';
import { listerPreparations, magasinDeLaGalerie } from './index.js';
import { preparerPresentation, type ProgressionPresentation } from './preparation.js';
import styles from '../Presentation.module.css';

export function usePreparationsPresentation(actif: boolean) {
  const { galerieSlug, compte } = useShellContext();
  return useQuery({
    queryKey: cleMetier(galerieSlug, 'presentation', 'preparations'),
    queryFn: () => listerPreparations({ compteId: compte.id, galerieSlug }),
    enabled: actif,
    networkMode: 'always',
    staleTime: 0,
  });
}
export function DisponibilitePresentation({ selectionId }: { selectionId: string }) {
  const { t, i18n } = useTranslation('presentation');
  const { galerie } = useShellContext();
  const autorisee =
    galerieAModule(galerie, 'rendez-vous-client') && galerieALaction(galerie, 'presentation:read');
  const preparations = usePreparationsPresentation(autorisee);
  const preparation = preparations.data?.find((candidate) => candidate.selectionId === selectionId);
  if (!autorisee || !preparation) return null;
  return (
    <p className={styles.aide}>
      {t('horsLigne.disponible', {
        date: formatDate(new Date(preparation.expireLe), i18n.language as Langue, 'court'),
      })}
    </p>
  );
}
export function PreparationSelection({ selectionId }: { selectionId: string }) {
  const { t, i18n } = useTranslation('presentation');
  const { galerieSlug, galerie, compte } = useShellContext();
  const client = useQueryClient();
  const [progression, setProgression] = useState<ProgressionPresentation | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  if (
    !galerieAModule(galerie, 'rendez-vous-client') ||
    !galerieALaction(galerie, 'presentation:read')
  )
    return null;
  async function preparer() {
    const session = client.getQueryData<SessionUtilisateur>(CLE_SESSION);
    if (!session || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await preparerPresentation(
        magasinDeLaGalerie(session, galerieSlug),
        { compteId: compte.id, galerieSlug },
        selectionId,
        setProgression,
      );
      await client.invalidateQueries({
        queryKey: cleMetier(galerieSlug, 'presentation', 'preparations'),
      });
    } catch (cause) {
      setErreur(cause);
    } finally {
      setEnCours(false);
    }
  }
  return (
    <div className={styles.page}>
      <Button
        variant="secondary"
        loading={enCours}
        disabled={!navigator.onLine}
        onClick={() => void preparer()}
      >
        {t('horsLigne.preparer')}
      </Button>
      {progression ? (
        <div role="status" aria-live="polite">
          {enCours ? (
            <progress
              aria-label={t('horsLigne.preparer')}
              max={Math.max(1, progression.total)}
              value={progression.faites}
            />
          ) : null}
          <p>
            {t('horsLigne.progression', {
              faites: progression.faites,
              total: progression.total,
              taille: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(
                progression.octets / 1024 / 1024,
              ),
            })}
          </p>
        </div>
      ) : null}
      <DisponibilitePresentation selectionId={selectionId} />
      <ErreurPresentation erreur={erreur} />
    </div>
  );
}
